/**
 * Settings → Cihazlar.
 *
 * The screen exists for one moment: somebody sold a television, or lost a phone, and wants
 * it out of the household now. So "Erişimi kaldır" is the action the layout is built
 * around, and it asks for confirmation because it cannot be undone from here — a revoked
 * device stays revoked even if it pairs again, which is the whole point.
 */
import { useCallback, useEffect, useState } from 'react';
import type { DeviceDto } from '@filmodasi/shared';
import { api } from '../api/client';
import { Empty, ErrorBox, Spinner } from '../components/ui';
import { useConfirm } from '../components/Dialog';

const TYPE_LABELS: Record<string, string> = {
  tv: 'Televizyon',
  phone: 'Telefon',
  tablet: 'Tablet',
  desktop: 'Bilgisayar',
};

/** "3 dakika önce" — the only fact anyone reads on this screen at a glance. */
function relativeTime(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'az önce';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} dakika önce`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} saat önce`;
  const days = Math.round(hours / 24);
  return `${days} gün önce`;
}

function DeviceRow({
  device,
  onChanged,
}: {
  device: DeviceDto;
  onChanged: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(device.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { confirm, dialog } = useConfirm();

  const rename = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === device.name) {
      setRenaming(false);
      setName(device.name);
      return;
    }
    setBusy(true);
    try {
      await api(`/api/devices/${device.id}`, { method: 'PATCH', body: { name: trimmed } });
      setRenaming(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Yeniden adlandırılamadı');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    // Deliberately a confirm: this is the one irreversible action in the app that a
    // mis-tap can reach, and the device is often in another room.
    const ok = await confirm({
      title: `“${device.name}” cihazının erişimi kaldırılsın mı?`,
      body: 'Cihaz anında oturumdan düşer ve yeniden bağlanmak için tekrar eşleştirilmesi gerekir.',
      confirmLabel: 'Erişimi kaldır',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api(`/api/devices/${device.id}`, { method: 'DELETE' });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erişim kaldırılamadı');
    } finally {
      setBusy(false);
    }
  };

  const revoked = device.revokedAt !== null;

  return (
    <li className={`device${revoked ? ' device--revoked' : ''}`}>
      <div className="device__main">
        {renaming ? (
          <input
            className="device__rename"
            value={name}
            autoFocus
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => void rename()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void rename();
              if (e.key === 'Escape') {
                setName(device.name);
                setRenaming(false);
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="device__name"
            disabled={revoked}
            onClick={() => setRenaming(true)}
          >
            {device.name}
          </button>
        )}

        <span className="device__meta">
          {TYPE_LABELS[device.deviceType] ?? device.deviceType}
          {device.platform ? ` · ${device.platform}` : ''}
        </span>

        <span className="device__meta">
          {revoked
            ? `Erişim kaldırıldı · ${relativeTime(device.revokedAt!)}`
            : device.online
              ? `Çevrimiçi${device.currentProfileName ? ` · ${device.currentProfileName}` : ''}`
              : `Son görülme ${relativeTime(device.lastSeenAt)}`}
        </span>
      </div>

      <div className="device__side">
        {!revoked && <span className={`dot${device.online ? ' dot--on' : ''}`} aria-hidden />}
        {!revoked && (
          <button type="button" className="link link--danger" disabled={busy} onClick={() => void revoke()}>
            Erişimi kaldır
          </button>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      {dialog}
    </li>
  );
}

export function DevicesPage() {
  const [devices, setDevices] = useState<DeviceDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api<{ items: DeviceDto[] }>('/api/devices', { withoutProfile: true })
      .then((data) => {
        setDevices(data.items);
        setError(null);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Cihazlar yüklenemedi'));
  }, []);

  useEffect(load, [load]);

  return (
    <main className="devices">
      <h1>Cihazlar</h1>
      <p className="devices__intro">
        Bu hesaba bağlı cihazlar. Bir cihazın erişimini kaldırdığınızda oturumu anında
        kapanır ve tekrar eşleşse bile geri açılmaz.
      </p>

      {error && <ErrorBox message={error} />}
      {!devices && !error && <Spinner />}
      {devices?.length === 0 && (
        <Empty>Henüz eşleşmiş cihaz yok. Televizyonunuzda “Telefonla giriş yap” deyin.</Empty>
      )}

      {devices && devices.length > 0 && (
        <ul className="devices__list">
          {devices.map((d) => (
            <DeviceRow key={d.id} device={d} onChanged={load} />
          ))}
        </ul>
      )}
    </main>
  );
}

export default DevicesPage;
